import { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { PropsWithClassName } from 'src/shared/utils/types/PropsWithClassName';

import { Label } from '@shared/forms/components/Label';
import { cn } from '@shared/utils/helpers';

import { useId } from './useId';

type FormFieldProps = PropsWithClassName<{
  name: string;
  label?: ReactNode;
  error?: string;
  required?: boolean;
  labelClassName?: string;
  errorClassName?: string;
  children: (props: FormFieldRenderProps) => ReactNode;
}>;

type FormFieldRenderProps<T = any> = {
  id: string;
  name: string;
  error?: string;
  required?: boolean;
  value: T;
  onChange: (value: T) => void;
};

export function FormField({
  name,
  label,
  required,
  labelClassName,
  errorClassName,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const fieldId = name ? `${name}-${id}` : id;

  const { control, formState } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className={cn('grid gap-2', className)}>
          <Label
            htmlFor={fieldId}
            error={formState.errors[field.name]?.message}
            className={labelClassName}
            required={required}
          >
            {label}
          </Label>
          {children({
            id: fieldId,
            name,
            error: formState.errors[field.name]?.message,
            required,
            value: field.value,
            onChange: field.onChange,
          })}
          {formState.errors[field.name] && (
            <label htmlFor={fieldId} className={cn('text-red-600 font-normal text-sm', errorClassName)}>
              {formState.errors[field.name]?.message}
            </label>
          )}
        </div>
      )}
    />
  );
}

export type WithFormField<T extends object> = PropsWithClassName<T> &
  Omit<FormFieldProps, 'className' | 'children' | 'value' | 'onChange'>;
